import { ReactElement, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../controls/Button';
import CheckboxControl from '../controls/Checkbox';
import { initiatePaypalPayment } from '../services/dashboard-service';
import { isAdminRole } from '../services/session-storage-service';
import usePlan, { DevicePlan, PLAN_PRICES_USD } from '../hooks/usePlan';

type PurchasablePlan = 'Basic' | 'Advanced' | 'Professional';

// eslint-disable-next-line jsdoc/require-jsdoc
function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

// eslint-disable-next-line jsdoc/require-jsdoc
function planStatusClass(plan: DevicePlan): string {
  if (plan.planStatus === 'grace-period' || plan.planStatus === 'expired') return 'text-error-400';
  if (plan.planStatus === 'expiring-soon') return 'text-warning-300';
  return 'text-gray-500';
}

// eslint-disable-next-line jsdoc/require-jsdoc
function planSummary(plan: DevicePlan): string {
  if (!plan.subscribedPlan) return 'No plan';
  if (!plan.renewalDate) return `${plan.subscribedPlan} · no expiry`;
  const date = new Date(plan.renewalDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return `${plan.subscribedPlan} · ${plan.planStatus === 'expired' ? 'expired' : 'renews'} ${date}`;
}

/**
 * Pricing plan page. Plans are bought per device: pick the devices, then the plan.
 * @returns - A page to display pricing plans.
 */
export default function Pricing(): ReactElement {
  const { devicePlans } = usePlan();
  const [searchParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Only Admins may buy or change plans; the backend refuses anyone else.
  const canPurchase = isAdminRole();

  // Devices can be preselected from a link, e.g. the expiry banner's "Renew Now".
  const [selected, setSelected] = useState<Set<number>>(() => {
    const param = searchParams.get('devices');
    if (!param) return new Set();
    return new Set(
      param
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter((v) => !Number.isNaN(v))
    );
  });
  // Ignore preselected serials that are not this organisation's devices.
  const selectedSerials = useMemo(
    () => devicePlans.map((p) => p.serialNumber).filter((serial) => selected.has(serial)),
    [devicePlans, selected]
  );
  const selectedCount = selectedSerials.length;
  const allSelected = devicePlans.length > 0 && selectedCount === devicePlans.length;

  /**
   * Adds or removes a device from the selection.
   * @param serial - Device serial number.
   */
  const toggleDevice = (serial: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  };

  /** Selects every device, or clears the selection when all are selected. */
  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(devicePlans.map((p) => p.serialNumber)));
  };

  /**
   * Handles plan checkout for the selected devices by initiating PayPal payment flow.
   * @param planName - Plan to buy for every selected device.
   */
  const handlePlanCheckout = async (planName: PurchasablePlan): Promise<void> => {
    setErrorMessage(null);
    setLoadingPlan(planName);

    try {
      const approvalUrl = await initiatePaypalPayment({ planName, serialNumbers: selectedSerials });
      window.location.assign(approvalUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Unable to initiate PayPal payment', error);
      setErrorMessage('Unable to start PayPal checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  /**
   * Number of devices currently on a plan, for the "on this plan" hints.
   * @param planName - Plan name.
   * @returns Device count.
   */
  const devicesOnPlan = (planName: string): number =>
    devicePlans.filter((p) => p.effectivePlan === planName).length;

  /**
   * Price and checkout cell for one self-serve plan.
   * @param planName - Plan to render.
   * @returns Table cell.
   */
  const renderPlanCell = (planName: PurchasablePlan): ReactElement => {
    const price = PLAN_PRICES_USD[planName];
    const onPlan = devicesOnPlan(planName);
    // Buying the plan every selected device already has is a renewal.
    const isRenewal =
      selectedCount > 0 &&
      selectedSerials.every(
        (serial) => devicePlans.find((p) => p.serialNumber === serial)?.subscribedPlan === planName
      );
    return (
      <td className="pt-8 px-6 align-top">
        <div className="text-2xl font-semibold text-gray-300">{formatUsd(price)}</div>
        <div className="text-gray-500 text-sm mt-1">Per month per device</div>
        <div className="text-gray-400 text-sm mt-1 min-h-[1.25rem]">
          {selectedCount > 0
            ? `× ${selectedCount} device${selectedCount !== 1 ? 's' : ''} = ${formatUsd(
                price * selectedCount
              )}`
            : ''}
        </div>
        <div className="mt-3">
          <Button
            size="lg"
            theme="secondary-color"
            name={isRenewal ? 'Renew Plan' : 'Choose Plan'}
            onClick={() => handlePlanCheckout(planName)}
            loading={loadingPlan === planName}
            disabled={
              !canPurchase ||
              selectedCount === 0 ||
              (loadingPlan !== null && loadingPlan !== planName)
            }
          />
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {onPlan > 0 ? `${onPlan} device${onPlan !== 1 ? 's' : ''} on this plan` : ''}
        </div>
      </td>
    );
  };

  const check = <div className="material-icons-outlined text-success-500">check_circle</div>;
  const dash = <span className="text-gray-600">-</span>;

  const tdBase = 'font-normal text-gray-400 p-6 text-sm text-center';
  const tdLabel = 'font-medium text-gray-300 p-6 text-sm';

  return (
    <div className="my-6 mx-2 p-6 flex flex-1 flex-col bg-container-500 rounded">
      <div className="text-xl text-white font-semibold">We&apos;ve got plans just perfect for you!</div>
      <div className="text-gray-500 mt-1">
        Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt{' '}
      </div>
      {!canPurchase ? (
        <div className="mt-4 rounded border border-gray-700 px-4 py-3 text-sm text-gray-400">
          Only your organisation&apos;s Admin can purchase or change device plans.
        </div>
      ) : null}
      {errorMessage ? (
        <div
          role="alert"
          className="mt-4 rounded border border-error-500 bg-error-500/10 px-4 py-3 text-sm text-error-400"
        >
          {errorMessage}
        </div>
      ) : null}
      <section className="mt-6 rounded border border-gray-800">
        <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3">
          <div>
            <div className="text-gray-300 font-semibold">1. Choose devices</div>
            <div className="text-xs text-gray-500">
              Each device has its own plan and renewal date. Pick the devices, then a plan below.
            </div>
          </div>
          {canPurchase && devicePlans.length > 0 && (
            <CheckboxControl
              id="pricing-select-all"
              checked={allSelected}
              onChange={toggleAll}
              label={`  Select all (${selectedCount}/${devicePlans.length})`}
            />
          )}
        </div>
        {devicePlans.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-500">
            No devices yet. Add a device before choosing a plan.
          </div>
        ) : (
          <div className="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto p-4 sm:grid-cols-2 lg:grid-cols-3">
            {devicePlans.map((plan) => (
              <button
                type="button"
                key={plan.serialNumber}
                aria-pressed={selected.has(plan.serialNumber)}
                disabled={!canPurchase}
                onClick={() => toggleDevice(plan.serialNumber)}
                className={`flex items-center gap-3 rounded border px-3 py-2 text-left disabled:cursor-default ${
                  selected.has(plan.serialNumber)
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-gray-800 hover:border-gray-600'
                }`}
              >
                <span
                  aria-hidden
                  className={`material-icons-outlined text-xl ${
                    selected.has(plan.serialNumber) ? 'text-primary-500' : 'text-gray-600'
                  }`}
                >
                  {selected.has(plan.serialNumber) ? 'check_box' : 'check_box_outline_blank'}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm text-gray-300">
                    {plan.deviceName}{' '}
                    <span className="text-xs text-gray-500">#{plan.serialNumber}</span>
                  </div>
                  <div className={`text-xs ${planStatusClass(plan)}`}>
                    {planSummary(plan)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>
      <div className="mt-6 text-gray-300 font-semibold">2. Pick a plan</div>
      <table className="divide-y divide-gray-800 border border-gray-800 mt-3">
        <thead>
          <tr>
            <th className="text-xl text-gray-300 text-left px-6 font-semibold py-6 w-1/6">
              Pick a Plan
            </th>
            <th className="text-xl text-gray-300 text-left px-6 w-1/6">
              <div>Included</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div className="flex items-center">
                <div>Basic</div>
                <div className="ml-8 border border-primary-400 rounded-full py-1 px-3 text-sm text-primary-400">
                  Popular
                </div>
              </div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div>Advanced</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div>Professional</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
            <th className="text-xl text-gray-300 text-left px-6 py-6 w-1/6">
              <div>Enterprise</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Entry-Level</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Pricing row */}
          <tr>
            <td> </td>
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">Free</div>
              <div className="text-gray-500 text-sm mt-1">Per month per device</div>
              <div className="mt-4">
                <Button size="lg" theme="secondary-color" name="Choose Plan" disabled />
              </div>
            </td>
            {renderPlanCell('Basic')}
            {renderPlanCell('Advanced')}
            {renderPlanCell('Professional')}
            <td className="pt-8 px-6">
              <div className="text-2xl font-semibold text-gray-300">-</div>
              <div className="text-gray-500 text-sm mt-1">Email for quote</div>
              <div className="mt-4">
                <Button size="lg" theme="secondary-color" name="Choose Plan" disabled={!canPurchase} />
              </div>
            </td>
          </tr>

          {/* Key Features header */}
          <tr className="border-b border-b-gray-800">
            <td className="px-6 text-sm text-primary-600 font-semibold py-4" colSpan={6}>
              Key Features
            </td>
          </tr>

          {/* Number of users */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>number of users</td>
            <td className={`${tdBase} bg-background-500`}>1</td>
            <td className={`${tdBase} bg-background-500`}>1</td>
            <td className={`${tdBase} bg-background-500`}>1</td>
            <td className={`${tdBase} bg-background-500`}>3</td>
            <td className={`${tdBase} bg-background-500`}>unlimited</td>
          </tr>

          {/* Data Rate */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>Data Rate</td>
            <td className={`${tdBase} bg-container-500`}>1 minute data</td>
            <td className={`${tdBase} bg-container-500`}>1 minute data</td>
            <td className={`${tdBase} bg-container-500`}>10 second data</td>
            <td className={`${tdBase} bg-container-500`}>Real-time data</td>
            <td className={`${tdBase} bg-container-500`}>Real-time data</td>
          </tr>

          {/* Data download */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Data download</td>
            <td className={`${tdBase} bg-background-500`}>No download</td>
            <td className={`${tdBase} bg-background-500`}>7 day of data storage and download</td>
            <td className={`${tdBase} bg-background-500`}>1 month of data storage and download</td>
            <td className={`${tdBase} bg-background-500`}>3 months of data storage and download</td>
            <td className={`${tdBase} bg-background-500`}>Unlimited data storage</td>
          </tr>

          {/* Analytics */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>Analytics</td>
            <td className={`${tdBase} bg-container-500`}>No analytics</td>
            <td className={`${tdBase} bg-container-500`}>Data -&gt; Raw Data, Devices</td>
            <td className={`${tdBase} bg-container-500`}>
              Data -&gt; Raw Data and Analytics (1 month max) and Devices page
            </td>
            <td className={`${tdBase} bg-container-500`}>
              Data -&gt; Raw Data and Analytics (3 months) and devices page
            </td>
            <td className={`${tdBase} bg-container-500`}>
              All pages under data; Analytics for entire duration of data
            </td>
          </tr>

          {/* Alerts */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Alerts</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>1 User</td>
            <td className={`${tdBase} bg-background-500`}>3 Users</td>
            <td className={`${tdBase} bg-background-500`}>Unlimited</td>
          </tr>

          {/* Report */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>Report</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>Basic reports</td>
            <td className={`${tdBase} bg-container-500`}>Customized reports</td>
          </tr>

          {/* BACnet Integration */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>BACnet Integration</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>BACnet-ready</td>
            <td className={`${tdBase} bg-background-500`}>BACnet-ready</td>
          </tr>

          {/* Insights: Ventilation analytics */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>
              Insights: Ventilation analytics (including ACH calculation, source location, etc)
            </td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>AI/ML learner model for ventilation control</td>
            <td className={`${tdBase} bg-container-500`}>AI/ML learner model for ventilation control</td>
          </tr>

          {/* Insights */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Insights</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>Basic insights</td>
            <td className={`${tdBase} bg-background-500`}>Advanced insights</td>
          </tr>

          {/* AI summary */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>AI summary</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>Front page</td>
            <td className={`${tdBase} bg-container-500`}>through out the dashboard</td>
            <td className={`${tdBase} bg-container-500`}>Custom AI queries and summaries</td>
          </tr>

          {/* Tailored dashboard */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Tailored dashboard</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{check}</td>
          </tr>

          {/* Sensor replacement and maintenance program */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>
              Sensor replacement and maintenance program
            </td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{check}</td>
          </tr>

          {/* Real-time fault detection diagnosis */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500`}>Real-time fault detection diagnosis</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{dash}</td>
            <td className={`${tdBase} bg-background-500`}>{check}</td>
          </tr>

          {/* Data-driven guidance for ventilation system design */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-container-500`}>
              Data-driven guidance for ventilation system design
            </td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{dash}</td>
            <td className={`${tdBase} bg-container-500`}>{check}</td>
          </tr>

          {/* Chat to sales */}
          <tr className="border-b border-b-gray-800">
            <td className={`${tdLabel} bg-background-500 py-10`}> </td>
            <td className={`${tdBase} bg-background-500 py-10`}> </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
            <td className={`${tdBase} bg-background-500 py-10`}>
              <Button size="lg" theme="secondary-gray" name="Chat to sales" />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="mt-4 text-xs text-gray-500">
        Plans are billed per device per month, and each device can be on a different plan.
        Renewing a device&apos;s current plan early adds a month from its renewal date; switching
        plans starts the new plan today.
      </div>
    </div>
  );
}
